/**
 * Translates a click on an Insights chart into the Traces filter slice to jump
 * to (#1402). Pure + framework-free so it can be unit-tested and reused by every
 * chart surface; the calling view seeds the result into the Traces slice
 * (`seedTracesFilters`) and navigates to /traces.
 *
 * Every drill-down starts from the Insights slice (its date range, tag filters
 * and score filter — the "base") so the Traces view opens on the same slice the
 * user was looking at, then narrows to the clicked dimension:
 *   - the clicked criterion always becomes the sole criteria filter;
 *   - a histogram bin overrides the score range to that band;
 *   - a grouped line / heatmap cell adds (or replaces) the group tag;
 *   - a point on a time series narrows the date range to that bucket.
 */
import type {
  Bucket,
  CriteriaFilter,
  DateRangeFilter,
  ScoreFilter,
  TagFilter,
} from "../types";
import type { DrillDownTarget } from "./filterPersistence";
import type { DateWindow } from "./dateWindow";

/** The Insights filters a drill-down inherits from. */
export interface DrillBase {
  filters: TagFilter[];
  dateWindow?: DateWindow;
  scoreFilter?: ScoreFilter;
}

/** A discriminated description of what was clicked, emitted by each chart. */
export type DrillClick =
  | { type: "criteria"; criteria: string }
  | { type: "criteriaBucket"; criteria: string; bucketStart?: string; bucket: Bucket }
  | { type: "scoreBin"; criteria: string; minScore: number; maxScore: number }
  | { type: "group"; criteria: string; groupKey: string; groupValue: string }
  | {
      type: "groupBucket";
      criteria: string;
      groupKey: string;
      groupValue: string;
      bucketStart?: string;
      bucket: Bucket;
    };

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function ymdUTC(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/**
 * Map a bucket's start timestamp to the inclusive YYYY-MM-DD range it covers.
 * The date filter is day-granular, so an `hour` bucket collapses to its calendar
 * day. Parsed as UTC from the leading date so it never shifts across timezones.
 */
export function bucketToDateRange(
  bucketStart: string,
  bucket: Bucket,
): DateRangeFilter {
  const datePart = bucketStart.slice(0, 10); // YYYY-MM-DD
  const start = new Date(`${datePart}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return {};

  switch (bucket) {
    case "week": {
      const end = new Date(start);
      end.setUTCDate(end.getUTCDate() + 6);
      return { start_date: datePart, end_date: ymdUTC(end) };
    }
    case "month": {
      const first = new Date(
        Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1),
      );
      const last = new Date(
        Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0),
      );
      return { start_date: ymdUTC(first), end_date: ymdUTC(last) };
    }
    case "hour":
    case "day":
    default:
      return { start_date: datePart, end_date: datePart };
  }
}

/** The clicked bucket as a frozen window. */
export function bucketWindow(bucketStart: string, bucket: Bucket): DateWindow {
  const range = bucketToDateRange(bucketStart, bucket);
  return { kind: "absolute", start: range.start_date, end: range.end_date };
}

/** Add or replace the single-value tag filter for `key`. */
function upsertTag(filters: TagFilter[], key: string, value: string): TagFilter[] {
  return [...filters.filter((f) => f.key !== key), { key, values: [value] }];
}

/** Build the Traces filter target for a chart click, inheriting `base`. */
export function buildDrillDownTarget(
  base: DrillBase,
  click: DrillClick,
): DrillDownTarget {
  let filters = base.filters ?? [];
  // A clicked bucket is a specific period, so it becomes an absolute window —
  // it must not roll forward when the Traces page is reopened another day.
  let dateWindow = base.dateWindow;
  let scoreFilter = base.scoreFilter;
  const criteriaFilter: CriteriaFilter = { values: [click.criteria] };

  switch (click.type) {
    case "criteria":
      break;
    case "criteriaBucket":
      if (click.bucketStart) {
        dateWindow = bucketWindow(click.bucketStart, click.bucket);
      }
      break;
    case "scoreBin":
      scoreFilter = { min_score: click.minScore, max_score: click.maxScore };
      break;
    case "group":
      filters = upsertTag(filters, click.groupKey, click.groupValue);
      break;
    case "groupBucket":
      filters = upsertTag(filters, click.groupKey, click.groupValue);
      if (click.bucketStart) {
        dateWindow = bucketWindow(click.bucketStart, click.bucket);
      }
      break;
  }

  return { filters, criteriaFilter, dateWindow, scoreFilter };
}
